const API_URL = "https://api.zaimengineering.com";
// const API_URL = "http://localhost:8000";
const authToken = localStorage.getItem("authToken");
const AUTH_HEADER = {
  Authorization: `Bearer ${authToken}`,
  "Content-Type": "application/json",
};

// Extract the download filename from a Content-Disposition header.
// Handles both `filename="x.pdf"` and RFC 5987 `filename*=UTF-8''x.pdf`.
function filenameFromContentDisposition(header) {
  if (!header) return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  console.log(encoded, "encoded");
  if (encoded && encoded[1]) {
    try {
      return decodeURIComponent(encoded[1].replace(/^"|"$/g, "").trim());
    } catch (e) {
      // fall through to the plain filename form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain && plain[1]) return plain[1].trim();
  return null;
}

function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem("authToken");

  const headers = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  return fetch(url, { ...options, headers }).then(async (res) => {
    if (res.status === 401) {
      // Session expired -> clear auth (token, role, permissions) and route to login
      localStorage.clear();
      window.location.href = "/sign-in";
      return; // Stop further execution
    }

    if (!res.ok) {
      const errorText = await res.text();
      // Errors come back as { "error": "message" } JSON; fall back to raw text.
      let message = errorText || "API request failed";
      try {
        const parsed = JSON.parse(errorText);
        if (parsed && parsed.error) message = parsed.error;
      } catch (e) {
        // not JSON, keep the raw text
      }

      // 403 -> UI gating and server checks disagreed; surface a non-blocking toast app-wide.
      if (res.status === 403) {
        window.dispatchEvent(new CustomEvent("app:forbidden", { detail: message }));
      }

      throw new Error(message);
    }
    return res;
  });
}

const apis = {
  async login(username, password) {
    const res = await fetchWithAuth(`${API_URL}/auth/login`, {
      method: "POST",
      headers: AUTH_HEADER,
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) throw new Error("Failed to login");
    return res.json();
  },
  async getOrdersPerMonth() {
    const res = await fetchWithAuth(`${API_URL}/dashboard/orders-per-month`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch orders per month");
    return res.json();
  },
  async getPendingOrder() {
    const res = await fetchWithAuth(`${API_URL}/dashboard/pending-orders`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch orders");
    return res.json();
  },
  async getDashboardTotals() {
    const res = await fetchWithAuth(`${API_URL}/dashboard/totals`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch totals");
    return res.json();
  },
  async getCustomers() {
    const res = await fetchWithAuth(`${API_URL}/customers`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch totals");
    return res.json();
  },
  async getCustomerById(id) {
    const res = await fetchWithAuth(`${API_URL}/customers/${id}`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch totals");
    return res.json();
  },
  async updateCustomerById(id, payload) {
    const res = await fetchWithAuth(`${API_URL}/customers/${id}`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch customers");
    return res.json();
  },
  async createCustomer(newCustomerFields) {
    const res = await fetchWithAuth(`${API_URL}/customers`, {
      headers: AUTH_HEADER,
      method: "POST",
      body: JSON.stringify(newCustomerFields),
    });
    if (!res.ok) throw new Error("Failed to Create Customer");
    return res.json();
  },
  async quotationCreateUpdate(data, method, url) {
    // Expected request payload for combo quotations (per spec):
    // {
    //   quotationType: "combo",
    //   customerId: <number>,
    //   shipToAddressId: <number>,
    //   billToAddressId: <number>,
    //   items: [{
    //     productId: <number>,
    //     quantity: <number>,
    //     buyPrice: <number>,          // From combo's product
    //     sellingPrice: <number>       // Calculated from buyPrice + margin
    //   }],
    //   marginPercentage: <number>,    // NEW: from the selected combo
    //   discountAmount: <number>,      // NEW: fixed discount to apply
    //   transportAmount: <number>,
    //   // ... other fields
    // }
    // For non-combo quotations, marginPercentage and discountAmount are not required.
    const res = await fetchWithAuth(`${API_URL}/${url}`, {
      headers: AUTH_HEADER,
      method: method,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Failed to Create quotation");
    return res.json();
  },
  async getQuotationById(id) {
    // Expected response format for combo quotations (per spec):
    // {
    //   id, quotationType: "combo",
    //   customerId: <number>,
    //   shipToAddressId: <number>,
    //   shipToAddress: { id, street, city, state, pincode, ... },  // NEW: full object, not just ID
    //   billToAddressId: <number>,
    //   billToAddress: { id, street, city, state, pincode, ... },  // NEW: full object, not just ID
    //   marginPercentage: <number>,    // NEW: from the combo
    //   discountAmount: <number>,      // NEW: discount applied
    //   transportAmount: <number>,
    //   items: [{
    //     productId: <number>,
    //     quantity: <number>,
    //     buyPrice: <number>,
    //     sellingPrice: <number>,
    //     itemTotal: <number>
    //   }],
    //   subtotal: <number>,            // Sum of all item totals
    //   discountAmount: <number>,      // Amount discounted
    //   transportAmount: <number>,
    //   subtotalAfterDiscountAndTransport: <number>,  // (subtotal - discount) + transport
    //   gst: <number>,                 // 18% of subtotalAfterDiscountAndTransport
    //   grandTotal: <number>           // subtotalAfterDiscountAndTransport + gst
    // }
    const res = await fetchWithAuth(`${API_URL}/quotations/${id}`, {
      headers: AUTH_HEADER,
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to get quotation");
    return res.json();
  },
  async deleteQuotation(id) {
    const res = await fetchWithAuth(`${API_URL}/quotations/${id}`, {
      method: "DELETE",
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to delete quotation");
    return res.json();
  },
  async convertPi(id, date, transporterName) {
    const res = await fetchWithAuth(`${API_URL}/quotations/convert-pi/${id}`, {
      headers: AUTH_HEADER,
      method: "PUT",
      body: JSON.stringify({ expectedDeliveryDate: date, transporterName: transporterName }),
    });
    if (!res.ok) throw new Error("Failed to update quotation to PI");
    return res.json();
  },
  async getAllQuotation() {
    const res = await fetchWithAuth(`${API_URL}/quotations`, {
      headers: AUTH_HEADER,
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to get quotation");
    return res.json();
  },
  async getProducts() {
    const res = await fetchWithAuth(`${API_URL}/products`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },
  async getComboAndProducts() {
    // Expected response format (per spec):
    // {
    //   combos: [{
    //     id, name, shortName,
    //     marginPercentage: <number>,  // NEW: required for pricing calculations
    //     products: [{
    //       id, productName,
    //       buyPrice: <number>,        // NEW: required for margin-based selling price calc
    //       sellingPrice: <number>,
    //       quantity: <number>
    //     }]
    //   }],
    //   products: [...]
    // }
    const res = await fetchWithAuth(`${API_URL}/comboAndProduct`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },
  async getProductById(id) {
    const res = await fetchWithAuth(`${API_URL}/products/${id}`, {
      method: "GET",
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },
  async createProduct(payload) {
    const res = await fetchWithAuth(`${API_URL}/products`, {
      method: "POST",
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },
  async updateProductById(id, payload) {
    const res = await fetchWithAuth(`${API_URL}/products/${id}`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch products");
    return res.json();
  },
  async getCombos() {
    const res = await fetchWithAuth(`${API_URL}/combos`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch combo");
    return res.json();
  },
  async getCombosById(id) {
    // Expected response format (per spec):
    // {
    //   id, name, shortName,
    //   marginPercentage: <number>,  // NEW: required for quotation pricing
    //   products: [{
    //     id, productName,
    //     buyPrice: <number>,        // NEW: required for margin-based selling price calc
    //     sellingPrice: <number>,
    //     quantity: <number>
    //   }]
    // }
    const res = await fetchWithAuth(`${API_URL}/combos/${id}`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch combo");
    return res.json();
  },
  async comboUpdateOrCreate(payload, method, url) {
    // Expected request payload format (per spec):
    // {
    //   name: <string>,
    //   shortName: <string>,
    //   marginPercentage: <number>,  // NEW: required field for combo pricing
    //   // ... other fields
    // }
    // Payload should include marginPercentage when creating or updating combos.
    const res = await fetchWithAuth(`${API_URL}/${url}`, {
      method: method,
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch combo");
    return res.json();
  },
  async getUsers() {
    const res = await fetchWithAuth(`${API_URL}/users`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch Users");
    return res.json();
  },
  async getUserById(id) {
    const res = await fetchWithAuth(`${API_URL}/users/${id}`, {
      method: "GET",
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch Users");
    return res.json();
  },
  async createUser(payload) {
    const res = await fetchWithAuth(`${API_URL}/users`, {
      method: "POST",
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch Users");
    return res.json();
  },
  async updateUserById(id, payload) {
    const res = await fetchWithAuth(`${API_URL}/users/${id}`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch users");
    return res.json();
  },
  async assignRoleToUser(userId, roleId) {
    const res = await fetchWithAuth(`${API_URL}/users/${userId}/roles`, {
      method: "POST",
      headers: AUTH_HEADER,
      body: JSON.stringify({ roleId }),
    });
    if (!res.ok) throw new Error("Failed to assign role");
    return res.json();
  },
  async getOrders() {
    const res = await fetchWithAuth(`${API_URL}/orders`, {
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to fetch order");
    return res.json();
  },
  async completeOrder(id, date) {
    const res = await fetchWithAuth(`${API_URL}/orders/complete/${id}`, {
      headers: AUTH_HEADER,
      method: "PUT",
      body: JSON.stringify({ deliveryDate: date }),
    });
    if (!res.ok) throw new Error("Failed to update Order complete");
    return res.json();
  },
  // Move a not-yet-delivered order back to a Quotation (reverse of convertPi).
  async revertOrderToQuotation(id) {
    const res = await fetchWithAuth(`${API_URL}/orders/cancel/${id}`, {
      method: "PUT",
    });
    if (!res.ok) throw new Error("Failed to revert order to quotation");
    return res.json();
  },
  async sendAndDownloadInvoice(id) {
    const res = await fetchWithAuth(`${API_URL}/orders/send-invoice/${id}`, {
      headers: AUTH_HEADER,
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to update Order complete");
    // Prefer the attachment filename the backend sends via Content-Disposition.
    // NOTE: for cross-origin responses the server must expose this header via
    // `Access-Control-Expose-Headers: Content-Disposition`, else it reads as null.
    const filename =
      filenameFromContentDisposition(res.headers.get("Content-Disposition")) || "invoice.pdf";
    const blob = await res.blob();
    return { blob, filename };
  },
  // --- Auth: change password ---
  // NOTE: these newer methods intentionally do NOT pass AUTH_HEADER. AUTH_HEADER
  // captures the token at module-load time; fetchWithAuth injects the *current*
  // localStorage token instead, which matters for calls made before a page reload
  // (e.g. loadMyPermissions() runs right after login, before window.location.reload).
  async changePassword(payload) {
    const res = await fetchWithAuth(`${API_URL}/auth/change-password`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return res.json();
  },
  // --- Permissions ---
  async getMyPermissions() {
    const res = await fetchWithAuth(`${API_URL}/permissions/me`, {
      method: "GET",
    });
    return res.json();
  },
  async getPermissionModules() {
    const res = await fetchWithAuth(`${API_URL}/permissions/modules`, {
      method: "GET",
    });
    return res.json();
  },
  async getUserPermissions(userId) {
    const res = await fetchWithAuth(`${API_URL}/permissions/${userId}`, {
      method: "GET",
    });
    return res.json();
  },
  async updateUserPermissions(userId, permissions) {
    const res = await fetchWithAuth(`${API_URL}/permissions/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    });
    return res.json();
  },
  // --- Addresses ---
  async createAddress(customerId, addressData) {
    const res = await fetchWithAuth(`${API_URL}/customers/${customerId}/addresses`, {
      method: "POST",
      headers: AUTH_HEADER,
      body: JSON.stringify(addressData),
    });
    if (!res.ok) throw new Error("Failed to create address");
    return res.json();
  },
  async updateAddress(customerId, addressId, addressData) {
    const res = await fetchWithAuth(`${API_URL}/customers/${customerId}/addresses/${addressId}`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify(addressData),
    });
    if (!res.ok) throw new Error("Failed to update address");
    return res.json();
  },
  async deleteAddress(customerId, addressId) {
    const res = await fetchWithAuth(`${API_URL}/customers/${customerId}/addresses/${addressId}`, {
      method: "DELETE",
      headers: AUTH_HEADER,
    });
    if (!res.ok) throw new Error("Failed to delete address");
    return res.json();
  },
  async updateCustomerDefaults(customerId, { defaultShipToAddressId, defaultBillToAddressId }) {
    const res = await fetchWithAuth(`${API_URL}/customers/${customerId}`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify({ defaultShipToAddressId, defaultBillToAddressId }),
    });
    if (!res.ok) throw new Error("Failed to update customer defaults");
    return res.json();
  },
  // --- Notifications ---
  async getNotifications(limit = 20, offset = 0, isRead = null) {
    let url = `${API_URL}/notifications?limit=${limit}&offset=${offset}`;
    if (isRead !== null) url += `&isRead=${isRead}`;
    const res = await fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to fetch notifications");
    return res.json();
  },
  async getUnreadCount() {
    const res = await fetchWithAuth(`${API_URL}/notifications/unread-count`, {
      method: "GET",
    });
    if (!res.ok) throw new Error("Failed to fetch unread count");
    return res.json();
  },
  async markNotificationAsRead(id, isRead = true) {
    const res = await fetchWithAuth(`${API_URL}/notifications/${id}/read`, {
      method: "PUT",
      headers: AUTH_HEADER,
      body: JSON.stringify({ isRead }),
    });
    if (!res.ok) throw new Error("Failed to update notification");
    return res.json();
  },
};
export default apis;
